import React, { FunctionComponent } from 'react';
import { Platform, WriteDirection, DeviceType, Orientation } from 'sofa-reborn-react';
import SkapaIconButton from '@ingka/button';
import InformationCircleIconPaths from '@ingka/ssr-icon/paths/information-circle';
import TrashCanIconPaths from '@ingka/ssr-icon/paths/trash-can';
import RotateLeftIconPaths from '@ingka/ssr-icon/paths/rotate-left';
import ArrowLeftArrowRightIconPaths from '@ingka/ssr-icon/paths/arrow-left-arrow-right';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import CoverPopoverMenuContainer from '../../containers/CoverPopoverMenuContainer';
import { localeService } from '../../services';
import ArmrestPopoverButtonContainer from '../../containers/ArmrestPopoverButtonContainer';
import {
	KompisTooltip,
	KompisPopover,
	KompisPopoverPadding,
} from '@inter-ikea-kompis/react-components';
import { PopoverDirectionEnum } from '@inter-ikea-kompis/component-popover';
import {
	BackDrop,
	ButtonDiv,
	ColorOptionsWrapper,
	InfoRotateWrapper,
	RootDiv,
	StyledIconWrapper,
} from './PopoverMenuStyle';

type Props = {
	platform: Platform;
	deviceType: DeviceType;
	orientation: Orientation;
	writeDirection: WriteDirection;
	disconnectable: boolean;
	top: number;
	left: number;
	showRemoveButton?: boolean;
	showRotateButton?: boolean;
	showInfoButton?: boolean;
	showColorOptions?: boolean;
	showArmrestButton?: boolean;
	showBackrestMoveButton?: boolean;
	showBackrestAsArmrestButton?: boolean;
	disableBackrestAsArmrestMoveButton?: boolean;
	disableBackrestMoveButton?: boolean;
	onInfoClick?: () => void;
	onClickRemove?: () => void;
	onClickRotate?: () => void;
	onBackDropClick?: () => void;
	onBackrestMoveClick?: () => void;
	onBackrestAsArmrestClick?: () => void;
};

// eslint-disable-next-line @typescript-eslint/ban-types
const getPosition = (props: Props): {} => {
	return props.showColorOptions && props.deviceType === 'mobile' && props.orientation === 'portrait'
		? {
				position: 'absolute',
				bottom: '200px',
				width: '100%',
				zIndex: 5,
		  }
		: {
				position: 'absolute',
				top: props.top + 'px',
				left: props.left + 'px',
		  };
};

const PopoverMenu: FunctionComponent<Props> = (props) => {
	const {
		onInfoClick,
		onClickRemove,
		onClickRotate,
		onBackDropClick,
		onBackrestMoveClick,
		onBackrestAsArmrestClick,
		showRemoveButton,
		showRotateButton,
		showInfoButton,
		showColorOptions,
		showArmrestButton,
		showBackrestMoveButton,
		showBackrestAsArmrestButton,
		disableBackrestAsArmrestMoveButton,
		disableBackrestMoveButton,
		disconnectable,
	} = props;
	return (
		<div>
			{showColorOptions && props.deviceType === 'mobile' && props.orientation === 'portrait' && (
				<BackDrop onClick={(): void => onBackDropClick && onBackDropClick()}></BackDrop>
			)}
			<KompisPopover
				visible={true}
				theme={SkapaTheme}
				direction={
					props.showColorOptions && props.deviceType === 'mobile'
						? PopoverDirectionEnum.bottom
						: PopoverDirectionEnum.top
				}
				// @ts-expect-error Kompis elemenets support style but it isn't exposed
				style={getPosition(props)}
				zIndex={5}
			>
				<KompisPopoverPadding theme={SkapaTheme}>
					<RootDiv>
						{(showInfoButton || showRotateButton || showArmrestButton) && (
							<InfoRotateWrapper showColorOptions={showColorOptions as boolean}>
								{showInfoButton && (
									<ButtonDiv
										platform={props.platform}
										leftBorder={
											props.writeDirection === 'rtl' &&
											!showColorOptions &&
											(showRemoveButton || showRotateButton || showBackrestAsArmrestButton)
										}
										bottomBorder={showColorOptions && showRotateButton}
										marginRight={
											props.writeDirection === 'ltr' &&
											(showRemoveButton || showRotateButton || showBackrestAsArmrestButton)
										}
										marginLeft={props.writeDirection === 'rtl' && showColorOptions}
										paddingLeft={
											props.writeDirection === 'rtl' &&
											(showRemoveButton || showRotateButton) &&
											!showColorOptions
										}
										paddingBottom={showRotateButton && showColorOptions}
									>
										{props.deviceType === 'mobile' ||
										props.deviceType === 'tablet' ||
										props.platform === 'kiosk' ? (
											<SkapaIconButton
												iconOnly
												ssrIcon={InformationCircleIconPaths}
												type={'tertiary'}
												aria-label={localeService.getTranslation('tooltipInformation')}
												onClick={(): void => onInfoClick && onInfoClick()}
											/>
										) : (
											<KompisTooltip
												theme={SkapaTheme}
												text={localeService.getTranslation('tooltipInformation')}
												direction={PopoverDirectionEnum.top}
												zIndex={5}
											>
												<StyledIconWrapper>
													<SkapaIconButton
														iconOnly
														ssrIcon={InformationCircleIconPaths}
														type={'tertiary'}
														aria-label={localeService.getTranslation('tooltipInformation')}
														onClick={(): void => onInfoClick && onInfoClick()}
													/>
												</StyledIconWrapper>
											</KompisTooltip>
										)}
									</ButtonDiv>
								)}
								{showArmrestButton && <ArmrestPopoverButtonContainer />}
								{showRotateButton && (
									<ButtonDiv
										platform={props.platform}
										leftBorder={!showColorOptions && showInfoButton}
										paddingLeft={
											(props.writeDirection === 'ltr' && showInfoButton && !showColorOptions) ||
											(props.writeDirection === 'rtl' && showRemoveButton && !showColorOptions)
										}
										marginRight={
											(props.writeDirection === 'ltr' && (showRemoveButton || showColorOptions)) ||
											(props.writeDirection === 'rtl' && showInfoButton && !showColorOptions)
										}
										marginLeft={props.writeDirection === 'rtl' && showColorOptions}
										paddingTop={showInfoButton && showColorOptions}
									>
										{props.deviceType === 'mobile' ||
										props.deviceType === 'tablet' ||
										props.platform === 'kiosk' ? (
											<SkapaIconButton
												iconOnly
												ssrIcon={RotateLeftIconPaths}
												type={'tertiary'}
												aria-label={localeService.getTranslation('tooltipRotateModule')}
												onClick={(): void => onClickRotate && onClickRotate()}
											/>
										) : (
											<KompisTooltip
												theme={SkapaTheme}
												text={localeService.getTranslation('tooltipRotateModule')}
												direction={PopoverDirectionEnum.top}
												zIndex={5}
											>
												<StyledIconWrapper>
													<SkapaIconButton
														iconOnly
														ssrIcon={RotateLeftIconPaths}
														type={'tertiary'}
														aria-label={localeService.getTranslation('tooltipRotateModule')}
														onClick={(): void => onClickRotate && onClickRotate()}
													/>
												</StyledIconWrapper>
											</KompisTooltip>
										)}
									</ButtonDiv>
								)}
							</InfoRotateWrapper>
						)}
						{showColorOptions && (
							<ColorOptionsWrapper
								writeDirection={props.writeDirection}
								showInfoButton={showInfoButton as boolean}
								showRotateButton={showRotateButton as boolean}
								showRemoveButton={showRemoveButton as boolean}
							>
								<CoverPopoverMenuContainer></CoverPopoverMenuContainer>
							</ColorOptionsWrapper>
						)}
						{showBackrestMoveButton && (
							<ButtonDiv platform={props.platform}>
								{props.deviceType === 'mobile' ||
								props.deviceType === 'tablet' ||
								props.platform === 'kiosk' ? (
									<SkapaIconButton
										iconOnly
										ssrIcon={ArrowLeftArrowRightIconPaths}
										type={'tertiary'}
										aria-label={localeService.getTranslation('tooltipChangePosition')}
										onClick={(): void => onBackrestMoveClick && onBackrestMoveClick()}
										disabled={disableBackrestMoveButton}
									/>
								) : (
									<KompisTooltip
										theme={SkapaTheme}
										text={localeService.getTranslation('tooltipChangePosition')}
										direction={PopoverDirectionEnum.top}
										zIndex={5}
									>
										<StyledIconWrapper>
											<SkapaIconButton
												iconOnly
												ssrIcon={ArrowLeftArrowRightIconPaths}
												type={'tertiary'}
												aria-label={localeService.getTranslation('tooltipChangePosition')}
												onClick={(): void => onBackrestMoveClick && onBackrestMoveClick()}
												disabled={disableBackrestMoveButton}
											/>
										</StyledIconWrapper>
									</KompisTooltip>
								)}
							</ButtonDiv>
						)}
						{showBackrestAsArmrestButton && (
							<ButtonDiv
								platform={props.platform}
								leftBorder={!showColorOptions && showInfoButton}
								paddingLeft={
									(props.writeDirection === 'ltr' && showInfoButton && !showColorOptions) ||
									(props.writeDirection === 'rtl' && showRemoveButton && !showColorOptions)
								}
								marginRight={
									(props.writeDirection === 'ltr' && (showRemoveButton || showColorOptions)) ||
									(props.writeDirection === 'rtl' && showInfoButton && !showColorOptions)
								}
								marginLeft={props.writeDirection === 'rtl' && showColorOptions}
								paddingTop={showInfoButton && showColorOptions}
							>
								{props.deviceType === 'mobile' ||
								props.deviceType === 'tablet' ||
								props.platform === 'kiosk' ? (
									<SkapaIconButton
										iconOnly
										ssrIcon={RotateLeftIconPaths}
										type={'tertiary'}
										aria-label={localeService.getTranslation('tooltipRotateModule')}
										onClick={(): void => onBackrestAsArmrestClick && onBackrestAsArmrestClick()}
										disabled={disableBackrestAsArmrestMoveButton}
									/>
								) : (
									<KompisTooltip
										theme={SkapaTheme}
										text={localeService.getTranslation('tooltipRotateModule')}
										direction={PopoverDirectionEnum.top}
										zIndex={5}
									>
										<StyledIconWrapper>
											<SkapaIconButton
												iconOnly
												ssrIcon={RotateLeftIconPaths}
												type={'tertiary'}
												aria-label={localeService.getTranslation('tooltipRotateModule')}
												onClick={(): void => onBackrestAsArmrestClick && onBackrestAsArmrestClick()}
												disabled={disableBackrestAsArmrestMoveButton}
											/>
										</StyledIconWrapper>
									</KompisTooltip>
								)}
							</ButtonDiv>
						)}
						{showRemoveButton && (
							<ButtonDiv
								platform={props.platform}
								leftBorder={
									(props.writeDirection === 'ltr' && showRotateButton && !showColorOptions) ||
									(props.writeDirection === 'ltr' &&
										!showRotateButton &&
										showInfoButton &&
										!showColorOptions)
								}
								paddingLeft={
									(props.writeDirection === 'ltr' && (showInfoButton || showRotateButton)) ||
									(props.writeDirection === 'ltr' && showColorOptions && !showRotateButton) ||
									(props.writeDirection === 'ltr' &&
										!showInfoButton &&
										!showColorOptions &&
										!showRotateButton &&
										showArmrestButton)
								}
								marginRight={props.writeDirection === 'rtl' && (showInfoButton || showRotateButton)}
								alignSelfCenter={showColorOptions}
							>
								{props.deviceType === 'mobile' ||
								props.deviceType === 'tablet' ||
								props.platform === 'kiosk' ? (
									<SkapaIconButton
										iconOnly
										ssrIcon={TrashCanIconPaths}
										type={'tertiary'}
										disabled={!disconnectable}
										aria-label={localeService.getTranslation('tooltipDeleteSection')}
										onClick={(): void => onClickRemove && onClickRemove()}
									/>
								) : (
									<KompisTooltip
										theme={SkapaTheme}
										text={localeService.getTranslation('tooltipDeleteSection')}
										direction={PopoverDirectionEnum.top}
										zIndex={5}
									>
										<StyledIconWrapper>
											<SkapaIconButton
												iconOnly
												ssrIcon={TrashCanIconPaths}
												type={'tertiary'}
												disabled={!disconnectable}
												aria-label={localeService.getTranslation('tooltipDeleteSection')}
												onClick={(): void => onClickRemove && onClickRemove()}
											/>
										</StyledIconWrapper>
									</KompisTooltip>
								)}
							</ButtonDiv>
						)}
					</RootDiv>
				</KompisPopoverPadding>
			</KompisPopover>
		</div>
	);
};

export default PopoverMenu;
